import React from 'react';

/**
 * FirmList — a single card design for firms that reflows between two modes:
 *   • full-width (compact=false): wide cards, essentials spread across the row
 *   • rail (compact=true): narrow stacked cards for the master-detail rail
 *
 * Cards show: logo · name · primary office · employees.
 * Selected card gets an emerald left-bar + tint.
 *
 * Props:
 *   firms          — array of firm objects
 *   compact        — rail mode when true
 *   selectedId     — currently selected firm id (or null)
 *   onSelect(firm) — click a card
 *   onEditClick    — optional hover-reveal edit affordance
 *   selectedIds    — Set of bulk-selected ids (checkboxes)
 *   onToggleSelect(id, index, shiftKey)
 */
export default function FirmList({
  firms,
  compact = false,
  selectedId = null,
  onSelect,
  onEditClick,
  selectedIds = new Set(),
  onToggleSelect,
}) {
  const primaryOffice = (firm) => {
    const o = firm.offices?.find(x => x.isPrimary) || firm.offices?.[0];
    if (!o) return null;
    return [o.city, o.state, o.country].filter(Boolean).join(', ');
  };

  const initials = (name) => name?.split(/\s+/).map(w => w[0]).join('').substring(0, 2).toUpperCase();

  return (
    <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
      {firms.map((firm, index) => {
        const isActive = selectedId === firm.id;
        const isChecked = selectedIds.has(firm.id);
        const office = primaryOffice(firm);

        return (
          <div
            key={firm.id}
            onClick={() => onSelect?.(firm)}
            className={`group cursor-pointer border-b border-gray-100 transition-colors ${
              isActive
                ? `bg-emerald-50 ${compact ? 'sticky top-0 z-10 shadow-sm' : ''}`
                : isChecked ? 'bg-emerald-100/50' : 'bg-white/60 hover:bg-emerald-50/50'
            }`}
            style={isActive ? { boxShadow: 'inset 3px 0 0 #0F6E56' } : undefined}
          >
            {compact ? (
              /* ---- Rail card (stacked) ---- */
              <div className="flex items-center gap-2.5 px-3 py-2.5">
                {onToggleSelect && (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); onToggleSelect(firm.id, index, e.nativeEvent.shiftKey); }}
                    className="w-4 h-4 rounded border-gray-300 accent-emerald-600 cursor-pointer flex-shrink-0"
                  />
                )}
                {firm.logoUrl ? (
                  <img src={firm.logoUrl} alt={firm.name}
                    className="w-8 h-8 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : null}
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-[10px] font-bold items-center justify-center flex-shrink-0 ${firm.logoUrl ? 'hidden' : 'flex'}`}>
                  {initials(firm.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">{firm.name}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {office || '—'}
                    {firm.totalEmployees > 0 && <span className="text-gray-400"> · {firm.totalEmployees.toLocaleString()} emp</span>}
                  </div>
                </div>
              </div>
            ) : (
              /* ---- Full-width card ---- */
              <div className="flex items-center gap-3 px-4 py-2.5">
                {onToggleSelect && (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); onToggleSelect(firm.id, index, e.nativeEvent.shiftKey); }}
                    className="w-4 h-4 rounded border-gray-300 accent-emerald-600 cursor-pointer flex-shrink-0"
                  />
                )}
                {firm.logoUrl ? (
                  <img src={firm.logoUrl} alt={firm.name}
                    className="w-8 h-8 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : null}
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-[10px] font-bold items-center justify-center flex-shrink-0 ${firm.logoUrl ? 'hidden' : 'flex'}`}>
                  {initials(firm.name)}
                </div>

                {/* Name (+ LinkedIn) */}
                <div className="flex items-center gap-1.5 min-w-0 w-64 flex-shrink-0">
                  <span className="text-sm font-medium text-gray-900 truncate">{firm.name}</span>
                  {firm.linkedinUrl && (
                    <a href={firm.linkedinUrl} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#0A66C2] hover:text-[#004182] flex-shrink-0" title="View on LinkedIn">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                  )}
                </div>

                {/* Primary office */}
                <div className="flex items-center gap-1.5 text-xs text-gray-600 min-w-0 flex-1">
                  {office ? (
                    <>
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      <span className="truncate">{office}</span>
                    </>
                  ) : <span className="text-gray-400">—</span>}
                </div>

                {/* Employees */}
                <div className="text-xs text-gray-600 w-28 flex-shrink-0 text-right tabular-nums">
                  {firm.totalEmployees ? `${firm.totalEmployees.toLocaleString()} employees` : '—'}
                </div>

                {/* Edit (hover) */}
                {onEditClick && (
                  <button onClick={(e) => { e.stopPropagation(); onEditClick(firm); }}
                    className="flex-shrink-0 text-gray-400 hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit firm">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
